import { format, parseISO, compareAsc } from "date-fns";
import { Button, LicensePlate } from "@rumbledirect/ui";
import { Progress, TableColumnsType, Tag } from "antd";
import { Link } from "react-router-dom";
import { Dossier } from "@rumble-it/common-lib/types/models/Dossier.interface";
import { NavLink } from "react-router-dom";
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  EyeOutlined,
} from "@ant-design/icons";
import { getFormattedDossierType } from "@/lib/formatter/dossiers";

const getProgressFromId = (id: string): number => {
  if (id === "019a746e-bfe2-7fd6-86fe-c19cc4b6bb9b") return 50;

  let hash = 0;
  for (let i = 0; i < id.length; i++) {
    const char = id.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash;
  }
  return Math.abs(hash) % 101; // Returns 0-100
};

export const dossierUnreadColumn: TableColumnsType<Dossier>[number] = {
  title: "Nieuw",
  dataIndex: "adminFirstReadAt",
  key: "adminFirstReadAt",
  width: 80,
  fixed: true,
  render: (_: any, record) =>
    record.adminFirstReadAt === null ? <Tag color="green">Nieuw</Tag> : null,
};

export const dossierColumns: TableColumnsType<Dossier> = [
  {
    title: "Kenteken",
    dataIndex: "vehicle.licensePlate.identifier",
    key: "licensePlate",
    width: 150,
    fixed: true,
    render: (_: any, record) => (
      <NavLink to={`/dossiers/${record.id}`}>
        <LicensePlate country="NL" size="small">
          {record.vehicle?.licensePlate?.identifier}
        </LicensePlate>
      </NavLink>
    ),
  },
  {
    title: "Dossier type",
    dataIndex: "dossierType",
    key: "dossierType",
    render: (_: any, record) => getFormattedDossierType(record?.dossierType),
  },
  {
    title: "Bestuurder",
    dataIndex: "driver.person.fullName",
    key: "driver",
    render: (_: any, record) => record.driver?.person?.fullName,
  },
  {
    title: "Lease / Verzekeraar",
    dataIndex: "insurerInfo",
    key: "lease",
    render: (_: any, record) => {
      const insurerName = record.insurerName || record.insuranceCompany?.name;
      const leaseName = record.leaseCompany?.name;

      if (insurerName && leaseName) {
        return `${insurerName} / ${leaseName}`;
      }
      return insurerName || leaseName || "-";
    },
  },
  {
    title: "Hersteller",
    dataIndex: "servicePartners[0].name",
    key: "servicePartner",
    render: (_: any, record) => record.servicePartners[0]?.name,
  },
  {
    title: "ImageCapture",
    dataIndex: "imageCaptureSentStatus",
    key: "imageCaptureSentStatus",
    render: (_: any, record) => {
      const sentItems = record.imageCaptureSents || [];

      const totalCount = sentItems.length;
      const completedCount = sentItems.filter(
        (s: any) => s.completedAt !== null,
      ).length;
      const Icon =
        totalCount > 0 && totalCount === completedCount ? (
          <CheckCircleOutlined className="text-green-500" />
        ) : (
          <CloseCircleOutlined className="text-red-500" />
        );

      return (
        <span className="flex gap-2 items-center">
          {sentItems.length === 0 ? (
            "Niet verzonden"
          ) : (
            <>
              {Icon}({completedCount}/{totalCount})
            </>
          )}
        </span>
      );
    },
  },
  {
    title: "Aangemaakt op",
    dataIndex: "createdAt",
    key: "createdAt",
    render: (_: any, record) =>
      format(parseISO(record.createdAt), "dd-MM-yyyy HH:mm"),
    sorter: (a, b) => compareAsc(parseISO(a.createdAt), parseISO(b.createdAt)),
  },

  {
    title: "Acties",
    key: "actions",
    render: (_: any, record) => (
      <Link to={`/dossiers/${record.id}`}>
        <Button icon={<EyeOutlined />} variant="light" />
      </Link>
    ),
  },
];

export const claimDossierColumns: TableColumnsType<Dossier> = [
  {
    title: "Kenteken",
    dataIndex: "vehicle.licensePlate.identifier",
    key: "licensePlate",
    render: (_: any, record) => (
      <>
        <NavLink to={`/claims/dossiers/${record.id}`}>
          <LicensePlate country="NL">
            {record.vehicle?.licensePlate?.identifier}
          </LicensePlate>
        </NavLink>
      </>
    ),
  },
  {
    title: "Bestuurder",
    dataIndex: "driver.person.fullName",
    key: "driver",
    render: (_: any, record) => record.driver?.person?.fullName,
  },
  {
    title: "Hersteller",
    dataIndex: "servicePartners[0].name",
    key: "servicePartner",
    render: (_: any, record) => record.servicePartners?.[0]?.name,
  },
  {
    title: "Voortgang aangeleverde gegevens",
    key: "progress",
    render: (_: any, record) => (
      <Progress percent={getProgressFromId(record.id)} />
    ),
  },
  {
    title: "ImageCapture",
    dataIndex: "imageCaptureSentStatus",
    key: "imageCaptureSentStatus",
    render: (_: any, record) => {
      const sentItems = record.imageCaptureSents || [];

      const totalCount = sentItems.length;
      const completedCount = sentItems.filter(
        (s: any) => s.completedAt !== null,
      ).length;
      const Icon =
        totalCount > 0 && totalCount === completedCount ? (
          <CheckCircleOutlined className="text-green-500" />
        ) : (
          <CloseCircleOutlined className="text-red-500" />
        );

      return (
        <span className="flex gap-2 items-center">
          {sentItems.length === 0 ? (
            "Niet verzonden"
          ) : (
            <>
              {Icon}({completedCount}/{totalCount})
            </>
          )}
        </span>
      );
    },
  },
  {
    title: "Aangemaakt op",
    dataIndex: "createdAt",
    key: "createdAt",
    render: (_: any, record) =>
      format(parseISO(record.createdAt), "dd-MM-yyyy HH:mm"),
    sorter: (a, b) => compareAsc(parseISO(a.createdAt), parseISO(b.createdAt)),
  },

  {
    title: "Acties",
    key: "actions",
    render: (_: any, record) => (
      <Link to={`/claims/dossiers/${record.id}`}>
        <Button icon={<EyeOutlined />} variant="light" />
      </Link>
    ),
  },
];

export const agentDossierColumns: TableColumnsType<Dossier> = [
  {
    title: "Kenteken",
    dataIndex: "vehicle.licensePlate.identifier",
    key: "licensePlate",
    width: 100,
    render: (_: any, record) => (
      <>
        <NavLink to={`/agents/dossiers/${record.id}`}>
          <LicensePlate country="NL">
            {record.vehicle?.licensePlate?.identifier}
          </LicensePlate>
        </NavLink>
      </>
    ),
  },
  {
    title: "Bestuurder",
    dataIndex: "driver.person.fullName",
    key: "driver",
    render: (_: any, record) => record.driver?.person?.fullName,
  },
  {
    title: "Lease / Verzekeraar",
    dataIndex: "insurerInfo",
    key: "lease",
    render: (_: any, record) => {
      const insurerName = record.insurerName || record.insuranceCompany?.name;
      const leaseName = record.leaseCompany?.name;

      if (insurerName && leaseName) {
        return `${insurerName} / ${leaseName}`;
      }
      return insurerName || leaseName || "-";
    },
  },
  {
    title: "Hersteller",
    dataIndex: "servicePartners[0].name",
    key: "servicePartner",
    render: (_: any, record) => record.servicePartners?.[0]?.name,
  },
  {
    title: "ImageCapture",
    dataIndex: "imageCaptureSentStatus",
    key: "imageCaptureSentStatus",
    render: (_: any, record) => {
      const sentItems = record.imageCaptureSents || [];

      const totalCount = sentItems.length;
      const completedCount = sentItems.filter(
        (s: any) => s.completedAt !== null,
      ).length;
      const Icon =
        totalCount > 0 && totalCount === completedCount ? (
          <CheckCircleOutlined className="text-green-500" />
        ) : (
          <CloseCircleOutlined className="text-red-500" />
        );

      return (
        <span className="flex gap-2 items-center">
          {sentItems.length === 0 ? (
            "Niet verzonden"
          ) : (
            <>
              {Icon}({completedCount}/{totalCount})
            </>
          )}
        </span>
      );
    },
  },
  {
    title: "Voortgang aangeleverde gegevens",
    key: "progress",
    render: (_: any, record) => (
      <Progress percent={getProgressFromId(record.id)} />
    ),
  },
  {
    title: "Aangemaakt op",
    dataIndex: "createdAt",
    key: "createdAt",
    render: (_: any, record) =>
      format(parseISO(record.createdAt), "dd-MM-yyyy HH:mm"),
    sorter: (a, b) => compareAsc(parseISO(a.createdAt), parseISO(b.createdAt)),
  },

  {
    title: "Acties",
    key: "actions",
    render: (_: any, record) => (
      <Link to={`/agents/dossiers/${record.id}`}>
        <Button icon={<EyeOutlined />} variant="light" />
      </Link>
    ),
  },
];
